import React from "react";
import type { SupportCsatRow } from "@/features/accounts/accountsApi";
import { formatSupportWhen } from "./SupportCasesPanel";

export const CSAT_FACES: Record<number, string> = {
  1: "😠",
  2: "🙁",
  3: "😐",
  4: "🙂",
  5: "😄",
};

/** Ratings ≤ 2 are low satisfaction (follow-up suggested). */
export const CSAT_LOW_THRESHOLD = 2;

export function isLowCsat(rating: number | null | undefined): boolean {
  const n = Number(rating);
  return Number.isFinite(n) && n >= 1 && n <= CSAT_LOW_THRESHOLD;
}

/**
 * Staff view of the Client's Closed-Case satisfaction response. Read-only;
 * low ratings are highlighted for management follow-up. Never mutates the
 * Case (Closed stays Closed regardless of rating).
 */
export default function SupportCsatSummary({
  csat,
  ticketId,
}: {
  csat: SupportCsatRow | null | undefined;
  ticketId: string;
}) {
  if (!csat || !Number.isFinite(Number(csat.rating))) {
    return (
      <div
        className="small text-muted mt-2"
        data-testid={`support-csat-pending-${ticketId}`}
      >
        Client satisfaction: not yet rated.
      </div>
    );
  }
  const rating = Number(csat.rating);
  const low = csat.lowSatisfaction ?? isLowCsat(rating);
  return (
    <div
      className={`small rounded p-2 mt-2 border ${low ? "border-danger bg-danger-subtle" : "bg-white"}`}
      data-testid={`support-csat-${ticketId}`}
      data-low={low ? "true" : "false"}
    >
      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="fw-semibold">Client satisfaction:</span>
        <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>
          {CSAT_FACES[rating] || ""}
        </span>
        <span>{rating}/5</span>
        {low ? (
          <span className="badge text-bg-danger">Low satisfaction — follow up</span>
        ) : null}
        {csat.submittedAt ? (
          <span className="text-muted">{formatSupportWhen(csat.submittedAt)}</span>
        ) : null}
      </div>
      {csat.feedbackText ? (
        <div className="mt-1" style={{ whiteSpace: "pre-wrap" }}>
          “{csat.feedbackText}”
        </div>
      ) : null}
    </div>
  );
}
